/**
 * Manifest candidate-results.
 *
 * @remarks
 * 2 contextes :
 * - `candidate-results` (auth) : `/user-tests` et `/user-tests/:id`
 * - `tech-test-results` (public) : `/tests/:tid/eval/:eid/attempt/:aid`
 *
 * Le legacy importait `EvaluationDeadlineTimer` directement dans le Header
 * → fuite. Ici, headerSlot est exposé proprement par tech-tests.
 */

import { Award, BarChart2, HelpCircle, List, Search } from 'lucide-react';
import { CONTEXTS, lazyView, type FeatureManifest } from '@/app/registry';
import { candidateResultsRoutes, candidateResultsPublicRoutes } from './routes';

export const candidateResultsManifest: FeatureManifest = {
  id: 'candidate-results',
  contexts: [CONTEXTS.CANDIDATE_RESULTS, CONTEXTS.TECH_TEST_RESULTS],

  routes: [
    { zone: 'public', routes: candidateResultsPublicRoutes, errorFallbackLabel: 'Résultats' },
    { routes: candidateResultsRoutes, errorFallbackLabel: 'Résultats' },
  ],

  centerView: {
    [CONTEXTS.CANDIDATE_RESULTS]: lazyView(
      () => import('./views/UserTestEvaluationsView'),
      'UserTestEvaluationsView',
    ),
    [CONTEXTS.TECH_TEST_RESULTS]: lazyView(
      () => import('./views/EvaluationResultsSmartView'),
      'EvaluationResultsSmartView',
    ),
  },

  panels: {
    left: {
      tabs: {
        [CONTEXTS.CANDIDATE_RESULTS]: [
          {
            id: 'user-tests-nav',
            label: 'Tests',
            icon: List,
            view: lazyView(() => import('./views/UserTestsNavView'), 'UserTestsNavView'),
          },
        ],
        [CONTEXTS.TECH_TEST_RESULTS]: [
          {
            id: 'results-toc',
            label: 'Résultats',
            icon: BarChart2,
            view: lazyView(
              () => import('./views/EvaluationResultsTocView'),
              'EvaluationResultsTocView',
            ),
          },
          {
            id: 'results-search',
            label: 'Recherche',
            icon: Search,
            view: lazyView(
              () => import('./views/EvaluationResultsSearchView'),
              'EvaluationResultsSearchView',
            ),
          },
        ],
      },
    },
    right: {
      tabs: {
        [CONTEXTS.CANDIDATE_RESULTS]: [
          {
            id: 'results-summary',
            label: 'Résumé',
            icon: BarChart2,
            view: lazyView(() => import('./views/UserTestSummaryView'), 'UserTestSummaryView'),
          },
        ],
        [CONTEXTS.TECH_TEST_RESULTS]: [
          {
            id: 'results-guide',
            label: 'Guide',
            icon: HelpCircle,
            view: lazyView(
              () => import('./views/EvaluationResultsGuideView'),
              'EvaluationResultsGuideView',
            ),
          },
          // Temporairement masqué — à réactiver très prochainement
          // {
          //   id: 'results-share',
          //   label: 'Partager',
          //   icon: Share2,
          //   view: lazyView(
          //     () => import('./views/EvaluationResultsShareView'),
          //     'EvaluationResultsShareView',
          //   ),
          // },
        ],
      },
    },
  },

  navigation: [
    {
      id: 'candidate-results',
      label: 'Résultats',
      icon: Award,
      to: '/user-tests',
    },
  ],

  panelRequirements: {
    [CONTEXTS.CANDIDATE_RESULTS]: {
      left: { open: true, defaultActiveTab: 'user-tests-nav' },
      // Panneau droit masqué par défaut, révélable via le toggle header.
      right: { open: false, defaultActiveTab: 'results-summary' },
    },
    [CONTEXTS.TECH_TEST_RESULTS]: {
      left: { open: true, defaultActiveTab: 'results-toc' },
      right: { open: true, defaultActiveTab: 'results-guide' },
    },
  },
};
